import type { Socket } from 'socket.io-client'
import {
  SOCKET_EVENTS,
  parseAvatarConfig,
  type ChatHistoryPayload,
  type ChatMessagePayload,
  type CosmeticsSyncPayload,
  type GyGlancePayload,
  type GameEventsPayload,
  type GameOverPayload,
  type GameStatePayload,
  type MatchmakingFoundPayload,
  type PlayerAction,
  type RoomCreatedPayload,
  type RoomJoinedPayload,
  type RoomLeavePayload,
  type RoomSummary,
  type Seat,
  type DuelClockPayload,
  type SpectateListPayload,
} from '@shared/index'
import type { GameClient, GameClientHandlers } from './GameClient'
import { getClientId } from './clientId'
import { ensurePlayableDeckList } from '../localDeck'
import { usePlayerStore } from '../../stores/player'
import { useSiteStore } from '../../stores/site'
import { useDuelPrefsStore } from '../../stores/duelPrefs'
import { resolveDuelMatId } from '../gamemats'
import { resolveDuelSleeveId } from '../sleeves'
import { createAppSocket } from './createAppSocket'
import { publishClientRename, readAccountRename } from '../../account/liveRename'
import { releasePresenceHold } from './presenceHold'
import { isSocketTransportError } from './socketLink'

export type ConnectMode =
  | { kind: 'create'; username: string; vsAI?: boolean; invite?: string }
  | { kind: 'join'; code: string; username: string }
  | { kind: 'matchmaking'; username: string; callId?: string; casual?: boolean; event?: boolean }
  | { kind: 'tournament'; username: string; matchId: string; via?: string }

function pageInFront(): boolean {
  return document.visibilityState === 'visible'
}

/** Authoritative PvP client. Sends intents to the server and forwards its events/state. */

export class NetworkGameClient implements GameClient {
  private readonly socket: Socket
  private readonly handlerSets: GameClientHandlers[] = []
  private readonly clientId = getClientId()
  private readonly mode: ConnectMode
  private connectedOnce = false
  /** This device handed the duel to another one; do not steal it back. */
  private yielded = false
  private disposed = false
  /** One automatic return after this phone replaced its own socket. */
  private reclaimAttempts = 0
  private reclaimTimer: ReturnType<typeof setTimeout> | null = null
  /** Re-sends tournament:ready so a replaced socket stays the one the server seats. */
  private readyPulse: ReturnType<typeof setInterval> | null = null
  private lastResumeAt = 0
  /**
   * False until the server has this socket on the seat. Plays emitted while
   * disconnected would leave on socket.io's buffer and arrive before
   * `room:reconnect`, which the server answers with "not in this match".
   */
  private attached = false
  private outbound: PlayerAction[] = []
  /** Browser reported no network. A websocket can still look open. */
  private browserOffline = false
  seat: Seat = 0
  code: string | null = null
  get roomCode(): string | null {
    return this.code
  }
  readonly duelMatId: string
  readonly duelSleeveId: string
  lastAction: PlayerAction | null = null
  /**
   * Dice and VS run before Phaser exists. Hold the opening snapshot until
   * the scene asks for it, so those events are not dropped.
   */
  private parkedStart: GameStatePayload | null = null
  private parkedEvents: GameEventsPayload[] = []
  private parkedOver: GameOverPayload | null = null
  private boardAttached = false

  constructor(mode: ConnectMode) {
    this.mode = mode
    this.duelMatId = resolveDuelMatId(useDuelPrefsStore().matId)
    this.duelSleeveId = resolveDuelSleeveId(useDuelPrefsStore().sleeveId)
    this.socket = createAppSocket()
    this.registerListeners()
    this.bindBrowserLink()

    this.socket.on('connect_error', (err: Error) => {
      const message = err.message || 'connect_error'
      if (message === 'auth_required' || message.includes('auth_required')) {
        this.fire('onError', message)
        return
      }
      if (isSocketTransportError(message)) {
        this.fire('onLink', false)
        return
      }
      this.fire('onError', message)
    })

    this.socket.on('disconnect', (reason: string) => {
      this.attached = false
      if (this.disposed || this.yielded) return
      if (reason === 'io client disconnect') return
      this.fire('onLink', false)
      // The server dropped this socket because the same phone connected again.
      // Socket.IO will not reconnect on its own after that.
      if (reason === 'io server disconnect') this.armResume()
    })

    this.socket.on('connect', () => {
      if (this.yielded || this.disposed) return
      this.fire('onLink', true)
      // On a later (re)connect with a known room, try to resume our seat.
      if (this.connectedOnce && this.code) {
        this.emitReconnect()
        return
      }
      this.connectedOnce = true
      this.emitEntry()
    })
  }

  private emitReconnect(): void {
    if (!this.code) return
    this.socket.emit(SOCKET_EVENTS.ROOM_RECONNECT, {
      code: this.code,
      clientId: this.clientId,
      foreground: pageInFront(),
    })
  }

  private emitEntry(): void {
    const mode = this.mode
    const skipEventDeck =
      mode.kind === 'matchmaking' &&
      mode.event === true &&
      useSiteStore().eventQueue?.mode === 'random_deck'
    const deck = skipEventDeck ? null : ensurePlayableDeckList()
    const avatar = parseAvatarConfig(usePlayerStore().avatar)
    const matId = this.duelMatId
    const sleeveId = this.duelSleeveId
    const foreground = pageInFront()
    switch (mode.kind) {
      case 'create':
        this.socket.emit(SOCKET_EVENTS.ROOM_CREATE, {
          username: mode.username,
          vsAI: mode.vsAI ?? false,
          clientId: this.clientId,
          foreground,
          avatar,
          matId,
          sleeveId,
          ...(deck ? { deck } : {}),
          ...(mode.invite ? { invite: mode.invite } : {}),
        })
        break
      case 'join':
        this.socket.emit(SOCKET_EVENTS.ROOM_JOIN, {
          code: mode.code,
          username: mode.username,
          clientId: this.clientId,
          foreground,
          avatar,
          matId,
          sleeveId,
          ...(deck ? { deck } : {}),
        })
        break
      case 'matchmaking':
        this.socket.emit(SOCKET_EVENTS.MATCHMAKING_JOIN, {
          username: mode.username,
          clientId: this.clientId,
          foreground,
          avatar,
          matId,
          sleeveId,
          ...(deck ? { deck } : {}),
          ...(mode.casual ? { casual: true } : {}),
          ...(mode.event ? { event: true } : {}),
          ...(mode.callId && !mode.casual && !mode.event ? { callId: mode.callId } : {}),
        })
        break
      case 'tournament':
        this.socket.emit(SOCKET_EVENTS.TOURNAMENT_READY, {
          matchId: mode.matchId,
          clientId: this.clientId,
          ...(mode.via ? { via: mode.via } : {}),
        })
        this.armReadyPulse()
        break
    }
  }

  private resumeSeat(): void {
    if (this.disposed || this.yielded) return
    if (!pageInFront()) return
    if (this.attached && this.socket.connected) return
    const now = Date.now()
    if (now - this.lastResumeAt < 800) return
    this.lastResumeAt = now
    if (!this.socket.connected) {
      this.socket.connect()
      return
    }
    if (this.connectedOnce && this.code) {
      this.emitReconnect()
      return
    }
    if (this.connectedOnce) this.emitEntry()
  }

  /**
   * The page the player is looking at lost its socket to another connection
   * from this same phone. Try once to take the seat back.
   */
  private armResume(): void {
    if (this.reclaimAttempts >= 1 || this.reclaimTimer) return
    if (!pageInFront()) return
    this.reclaimTimer = setTimeout(() => {
      this.reclaimTimer = null
      if (this.disposed || this.yielded) return
      this.reclaimAttempts += 1
      this.resumeSeat()
    }, 600)
  }

  // Multiple consumers (the Vue overlay and the Phaser renderer) can subscribe.
  setHandlers(handlers: GameClientHandlers): void {
    this.handlerSets.push(handlers)
  }

  dropHandlers(): void {
    this.handlerSets.length = 0
  }

  takeParkedOpening(): {
    start: GameStatePayload | null
    events: GameEventsPayload[]
    over: GameOverPayload | null
  } {
    this.boardAttached = true
    const start = this.parkedStart
    const events = this.parkedEvents
    const over = this.parkedOver
    this.parkedStart = null
    this.parkedEvents = []
    this.parkedOver = null
    return { start, events, over }
  }

  private fire<K extends keyof GameClientHandlers>(
    key: K,
    ...args: Parameters<NonNullable<GameClientHandlers[K]>>
  ): void {
    for (const set of this.handlerSets) {
      const fn = set[key] as ((...a: typeof args) => void) | undefined
      fn?.(...args)
    }
  }

  send(action: PlayerAction): void {
    if (!this.code || this.yielded || this.disposed) return
    this.lastAction = action
    if (!this.attached || !this.socket.connected) {
      this.outbound.push(action)
      if (this.outbound.length > 8) this.outbound.shift()
      return
    }
    this.socket.emit(SOCKET_EVENTS.GAME_ACTION, { code: this.code, action })
  }

  private markAttached(): void {
    this.attached = true
    this.flushOutbound()
  }

  private flushOutbound(): void {
    if (!this.attached || !this.socket.connected || !this.code) return
    const code = this.code
    const batch = this.outbound.splice(0, this.outbound.length)
    for (const action of batch) {
      this.socket.emit(SOCKET_EVENTS.GAME_ACTION, { code, action })
    }
  }

  private dropOutbound(): void {
    this.outbound.length = 0
    this.attached = false
  }

  sendChat(text: string): void {
    if (!this.code) return
    this.socket.emit(SOCKET_EVENTS.CHAT_SEND, { code: this.code, text })
  }

  sendCosmetics(payload: { matId: string; sleeveId?: string }): void {
    if (!this.code) return
    this.socket.emit(SOCKET_EVENTS.COSMETICS_UPDATE, {
      code: this.code,
      matId: payload.matId,
      ...(payload.sleeveId ? { sleeveId: payload.sleeveId } : {}),
    })
  }

  sendGyGlance(whose: Seat, open: boolean): void {
    if (!this.code || this.watch) return
    this.socket.emit(SOCKET_EVENTS.GY_GLANCE, { code: this.code, whose, open })
  }

  requestState(): void {
    if (!this.code) return
    this.socket.emit(SOCKET_EVENTS.GAME_STATE_REQUEST, { code: this.code })
  }

  private armReadyPulse(): void {
    if (this.mode.kind !== 'tournament' || this.readyPulse) return
    const matchId = this.mode.matchId
    this.readyPulse = setInterval(() => {
      if (this.disposed || this.code || !this.socket.connected) return
      this.socket.emit(SOCKET_EVENTS.TOURNAMENT_READY, {
        matchId,
        clientId: this.clientId,
        ...(this.mode.kind === 'tournament' && this.mode.via ? { via: this.mode.via } : {}),
      })
    }, 10_000)
  }

  private stopReadyPulse(): void {
    if (!this.readyPulse) return
    clearInterval(this.readyPulse)
    this.readyPulse = null
  }

  dispose(opts?: { forfeit?: boolean }): void {
    if (this.disposed) return
    this.disposed = true
    this.stopReadyPulse()
    if (this.reclaimTimer) {
      clearTimeout(this.reclaimTimer)
      this.reclaimTimer = null
    }
    this.unbindBrowserLink()
    this.dropOutbound()
    releasePresenceHold(this.socket)
    const socket = this.socket
    const code = this.code
    this.code = null
    socket.io.reconnection(false)
    const finish = () => {
      socket.removeAllListeners()
      socket.disconnect()
    }
    if (!code) {
      finish()
      return
    }
    const payload: RoomLeavePayload = {}
    if (opts?.forfeit) payload.forfeit = true
    let closed = false
    const close = () => {
      if (closed) return
      closed = true
      finish()
    }
    // Ack first so a live duel forfeits before this socket drops.
    socket.timeout(800).emit(SOCKET_EVENTS.ROOM_LEAVE, payload, () => close())
  }

  /**
   * `offline` is immediate. Socket.IO still thinks the websocket is open
   * until its ping dies, so the board would stay quiet for half a minute.
   */
  private readonly onBrowserOffline = (): void => {
    if (this.disposed || this.yielded) return
    this.browserOffline = true
    this.attached = false
    this.fire('onLink', false)
  }

  /**
   * A half-open socket does not emit `disconnect`. Close it and connect
   * again so `room:reconnect` runs. `disconnect()` here is our own call;
   * that reason must not be what shows the banner.
   */
  private readonly onBrowserOnline = (): void => {
    if (this.disposed || this.yielded || !this.browserOffline) return
    this.browserOffline = false
    // iOS fires offline/online when you switch apps even if the duel socket
    // is still up. Tearing it down opens a second connection and the server
    // treats that as another device.
    if (this.socket.connected) {
      this.fire('onLink', true)
      if (this.code) {
        this.markAttached()
        this.requestState()
      }
      return
    }
    this.socket.connect()
  }

  private readonly onVisibility = (): void => {
    if (!pageInFront()) {
      this.reclaimAttempts = 0
      return
    }
    this.resumeSeat()
  }

  private bindBrowserLink(): void {
    window.addEventListener('offline', this.onBrowserOffline)
    window.addEventListener('online', this.onBrowserOnline)
    document.addEventListener('visibilitychange', this.onVisibility)
  }

  private unbindBrowserLink(): void {
    window.removeEventListener('offline', this.onBrowserOffline)
    window.removeEventListener('online', this.onBrowserOnline)
    document.removeEventListener('visibilitychange', this.onVisibility)
  }

  private registerListeners(): void {
    this.socket.on(SOCKET_EVENTS.ROOM_CREATED, (p: RoomCreatedPayload) => {
      this.code = p.room.code
      this.seat = p.seat
      this.markAttached()
      this.fire('onRoomUpdate', p.room)
    })
    this.socket.on(SOCKET_EVENTS.ROOM_JOINED, (p: RoomJoinedPayload) => {
      this.code = p.room.code
      this.seat = p.seat
      this.markAttached()
      this.fire('onRoomUpdate', p.room)
    })
    this.socket.on(SOCKET_EVENTS.ROOM_UPDATE, (room: RoomSummary) => {
      this.code = room.code
      this.fire('onRoomUpdate', room)
    })
    this.socket.on(SOCKET_EVENTS.MATCHMAKING_FOUND, (p: MatchmakingFoundPayload) => {
      this.code = p.code
      this.seat = p.seat
      this.stopReadyPulse()
      if (this.mode.kind === 'tournament') {
        this.emitReconnect()
        return
      }
      this.markAttached()
    })
    this.socket.on(SOCKET_EVENTS.GAME_START, (p: GameStatePayload) => {
      this.seat = p.viewerSeat
      this.markAttached()
      if (!this.boardAttached) {
        this.parkedStart = p
        this.parkedEvents = []
      }
      this.fire('onStart', p)
    })
    this.socket.on(SOCKET_EVENTS.GAME_EVENTS, (p: GameEventsPayload) => {
      if (!this.boardAttached) this.parkedEvents.push(p)
      this.fire('onEvents', p)
    })
    this.socket.on(SOCKET_EVENTS.GAME_STATE, (p: GameStatePayload) => {
      this.fire('onState', p)
    })
    this.socket.on(SOCKET_EVENTS.ROOM_ERROR, (p: { message: string; code?: string }) => {
      if (p.code === 'seatBusy') {
        this.fire('onLink', false)
        this.armResume()
        return
      }
      if (p.code === 'sessionMoved' || p.code === 'roomGone') this.dropOutbound()
      if (p.code === 'sessionMoved') {
        this.yielded = true
        this.socket.io.reconnection(false)
        this.socket.disconnect()
      }
      this.fire('onError', p.message, p.code)
    })
    this.socket.on(SOCKET_EVENTS.GAME_ERROR, (p: {
      message: string
      code?: string
      vars?: Record<string, string | number>
    }) => {
      this.fire('onError', p.message, p.code, p.vars)
    })
    this.socket.on(SOCKET_EVENTS.GAME_OVER, (p: GameOverPayload) => {
      if (!this.boardAttached) this.parkedOver = p
      this.fire('onOver', p)
    })
    this.socket.on(SOCKET_EVENTS.CHAT_MESSAGE, (p: ChatMessagePayload) => {
      this.fire('onChat', p)
    })
    this.socket.on(SOCKET_EVENTS.ACCOUNT_RENAME, (payload: unknown) => {
      const swap = readAccountRename(payload)
      if (!swap) return
      publishClientRename(swap.previous, swap.username)
    })
    this.socket.on(SOCKET_EVENTS.CHAT_HISTORY, (p: ChatHistoryPayload) => {
      this.fire('onChatHistory', p)
    })
    this.socket.on(SOCKET_EVENTS.COSMETICS_UPDATE, (p: CosmeticsSyncPayload) => {
      this.fire('onCosmetics', p)
    })
    this.socket.on(SOCKET_EVENTS.GY_GLANCE, (p: GyGlancePayload) => {
      this.fire('onGyGlance', p)
    })
    this.socket.on(SOCKET_EVENTS.GAME_CLOCK, (p: DuelClockPayload) => {
      this.fire('onClock', p)
    })
    this.socket.on(SOCKET_EVENTS.SPECTATE_LIST, (p: SpectateListPayload) => {
      this.fire('onSpectators', p)
    })
  }
}
